// Batch payout sagas.
//
// This is the only part of the module that moves money, so the effect choices
// matter more here than anywhere else:
//
//   - pay and confirm use makeLeadingWatcher: while a request is in flight,
//     further actions of the same type are simply never taken. Together with the
//     reducer's `submitting` flag (which disables the button) that makes a
//     double-click harmless. redux-saga 0.14 has no takeLeading; the equivalent
//     is a blocking `yield*` inside the watcher loop.
//   - nothing here retries a payment automatically. A failure is reported and
//     left to a human; a manual retry from the same dialog reuses its
//     idempotency key, so the service can recognise it as the same payment.
//   - after settlement the status is polled, raced against STOP so it ends on
//     unmount or on reaching a terminal status. Without the race a user closing
//     the page would leave a poll running in a background tab indefinitely.

import { call, fork, put, select, take } from 'redux-saga/effects';
import * as types from '../../lib/actionTypes.es6';
import * as actions from './actions.es6';
import {
  makeWatcher,
  makeLatestWatcher,
  makeLeadingWatcher,
  makePoller
} from '../../components/CommissionsShared/makeWatcher.es6';
import { isTerminalStatus } from '../../lib/commissionServiceEnums.es6';
import * as agentActions from '../CommissionAgents/actions.es6';
import {
  getCommissionAgents,
  getBatchPayouts,
  getBatchPayout,
  createBatchPayout,
  payBatchPayouts,
  confirmBatchPayout
} from '../../lib/requests.es6';

export var POLL_INTERVAL_MS = 5000;

// The list is scoped by status, and the response body does not say which status
// was asked for — so it is carried through on `meta` for the reducer to bucket.
function listSuccess(data, requestPayload) {
  return {
    type: types.GET_BATCH_PAYOUTS_SUCCESS,
    payload: data,
    meta: { status: requestPayload && requestPayload.status }
  };
}

function listFailed(error, requestPayload) {
  return {
    type: types.GET_BATCH_PAYOUTS_FAILURE,
    payload: error,
    meta: { status: requestPayload && requestPayload.status }
  };
}

export var getBatchPayoutsRequestWatcher = makeLatestWatcher(
  types.GET_BATCH_PAYOUTS_REQUEST,
  getBatchPayouts,
  listSuccess,
  listFailed
);

export var getBatchPayoutRequestWatcher = makeLatestWatcher(
  types.GET_BATCH_PAYOUT_REQUEST,
  getBatchPayout,
  actions.getBatchPayoutSuccess,
  actions.getBatchPayoutFailed,
  {
    args: function(payload) {
      return [payload && payload.guid ? payload.guid : payload];
    }
  }
);

// Creating a batch does not move money — it records an intention to pay — so the
// plain watcher is enough. It refreshes the list it belongs to on success.
function* refreshBatchList() {
  var params = yield select(function(state) {
    var slice = state.batchPayouts;
    return (slice && slice.listParams) || {};
  });
  if (params && params.status) {
    yield put(actions.getBatchPayoutsRequest(params));
  }
}

export var createBatchPayoutRequestWatcher = makeWatcher(
  types.CREATE_BATCH_PAYOUT_REQUEST,
  createBatchPayout,
  actions.createBatchPayoutSuccess,
  actions.createBatchPayoutFailed,
  { onSuccessEffects: refreshBatchList }
);

// Every held batch is fetched at once. Dispatching GET_BATCH_PAYOUT per guid
// would not do: that watcher is take-latest, so each would cancel the last.
// A failed lookup is swallowed — the error then just says "another pending
// batch" — and must not throw, or this watcher dies for the session.
export function* fetchHeldBatch(guid) {
  try {
    var response = yield call(getBatchPayout, guid);
    if (response && response.data) {
      yield put(actions.getBatchPayoutSuccess(response.data));
    }
  } catch (ignored) {
    // Deliberately quiet; see above.
  }
}

function* fetchHeldBatches(guids) {
  yield guids.map(function(guid) { return call(fetchHeldBatch, guid); });
}

export function* resolveHeldBatchPayoutsWatcher() {
  for (;;) {
    var action = yield take(types.RESOLVE_HELD_BATCH_PAYOUTS_REQUEST);
    var guids = (action.payload && action.payload.guids) || [];
    if (guids.length) yield fork(fetchHeldBatches, guids);
  }
}

// ─── The money ───────────────────────────────────────────────────────────────

export var payBatchPayoutsRequestWatcher = makeLeadingWatcher(
  types.PAY_BATCH_PAYOUTS_REQUEST,
  payBatchPayouts,
  actions.payBatchPayoutsSuccess,
  actions.payBatchPayoutsFailed,
  {
    args: function(payload) {
      return [payload.guid, payload.idempotencyKey];
    },
    // Settlement is asynchronous: the response says In_Progress, not Paid.
    // Re-read now so the stale Pending status cannot offer Pay again, then
    // poll until it settles.
    onSuccessEffects: function* startPolling(data, payload) {
      yield put(actions.getBatchPayoutRequest({ guid: payload.guid }));
      yield put(actions.startBatchPayoutPolling({ guid: payload.guid }));
    }
  }
);

export var confirmBatchPayoutRequestWatcher = makeLeadingWatcher(
  types.CONFIRM_BATCH_PAYOUT_REQUEST,
  confirmBatchPayout,
  actions.confirmBatchPayoutSuccess,
  actions.confirmBatchPayoutFailed,
  {
    args: function(payload) { return [payload.guid, payload.body]; },
    onSuccessEffects: function* refreshAfterConfirm(data, payload) {
      yield put(actions.getBatchPayoutRequest({ guid: payload.guid }));
      yield put(actions.startBatchPayoutPolling({ guid: payload.guid }));
    }
  }
);

// ─── Status polling ──────────────────────────────────────────────────────────

export var batchPayoutPollingWatcher = makePoller({
  startType: types.START_BATCH_PAYOUT_POLLING,
  stopType: types.STOP_BATCH_PAYOUT_POLLING,
  apiFn: getBatchPayout,
  intervalMs: POLL_INTERVAL_MS,
  args: function(payload) { return [payload.guid]; },
  onSuccess: actions.getBatchPayoutSuccess,
  onFailure: actions.getBatchPayoutFailed,
  isDone: function(batch) {
    return !!batch && isTerminalStatus(batch.status);
  },
  // Clears the `polling` flag when the loop ends on its own, not just when the
  // user navigates away.
  onStopped: actions.stopBatchPayoutPolling
});

// ─── Paying a whole agency ───────────────────────────────────────────────────

// GET /api/agents?agency_guid= lists an agency's agents; the records are also
// merged into the agents cache so the builder can label them. 100 is the
// service's page ceiling — an agency larger than that needs paging, which the
// builder does not do yet.
export var getAgencyAgentsRequestWatcher = makeWatcher(
  types.GET_AGENCY_AGENTS_REQUEST,
  getCommissionAgents,
  actions.getAgencyAgentsSuccess,
  actions.getAgencyAgentsFailed,
  {
    args: function(payload) {
      return [{ agency_guid: payload.agencyGuid, page: 1, size: 100 }];
    },
    onSuccessEffects: function*(data) {
      yield put(agentActions.resolveCommissionAgentsSuccess(data));
    }
  }
);
